import { ref, computed, onMounted, onUnmounted } from 'vue'
import Modal from '../Modal/Modal.vue'
import FilterTabs from '../FilterTabs/FilterTabs.vue'
import NykCheckbox from '../CustomForm/NykCheckbox/NykCheckbox.vue'
import NykButton from '../NykButton/NykButton.vue'

export default {
    name: 'PermissionBlock',
    components: { Modal, FilterTabs, NykCheckbox, NykButton },
    props: {
        languageItems: {
            type: Array,
            default: () => []
        },
        labels: {
            type: Object,
            default: () => ({})
        },
        consents: {
            type: Array,
            default: () => []
        },
        cookieName: {
            type: String,
            default: ''
        },
        cookieExpirationDays: {
            type: Number,
            default: 365
        },
        redirectUrl: {
            type: String,
            default: ''
        }
    },
    setup(props) {
        const modalOpen = ref(false)
        const tabsKey = ref(0)
        const currentLocale = ref(
            props.languageItems?.[0]?.languageCode || 'da-DK'
        )
        const cookieConsentGiven = ref(false)
        const consentValues = ref({})
        const statusMessage = ref('')
        let initialStateIntervalId = null
        let initialStateTimeoutId = null

        const permissionStorageKey = computed(() => props.cookieName || 'permission_block_consent')
        const acceptedValues = ['true', '1']

        const clearInitialStateTimers = () => {
            if (initialStateIntervalId) {
                window.clearInterval(initialStateIntervalId)
                initialStateIntervalId = null
            }

            if (initialStateTimeoutId) {
                window.clearTimeout(initialStateTimeoutId)
                initialStateTimeoutId = null
            }
        }

        const initConsentValues = () => {
            const values = {}
            props.consents.forEach(c => {
                values[c.id] = false
            })
            consentValues.value = values
        }

        const getCookieValue = (cookieName) => {
            const cookiePrefix = `${cookieName}=`
            const cookieEntry = document.cookie
                .split(';')
                .map(cookie => cookie.trim())
                .find(cookie => cookie.startsWith(cookiePrefix))

            return cookieEntry ? cookieEntry.substring(cookiePrefix.length) : ''
        }

        const getCookieInformationConsent = () => {
            const consentCookieValue = getCookieValue('CookieInformationConsent')

            if (!consentCookieValue) {
                return null
            }

            try {
                return JSON.parse(decodeURIComponent(consentCookieValue))
            } catch {
                return null
            }
        }

        const getPermissionCookieValue = () => {
            if (!props.cookieName) {
                return ''
            }

            return getCookieValue(props.cookieName)
        }

        const hasPermissionCookie = () => {
            return acceptedValues.includes(getPermissionCookieValue())
        }

        const getPermissionSessionValue = () => {
            if (!permissionStorageKey.value) {
                return ''
            }

            try {
                return window.sessionStorage.getItem(permissionStorageKey.value) || ''
            } catch {
                return ''
            }
        }

        const hasPermissionSessionValue = () => {
            return acceptedValues.includes(getPermissionSessionValue())
        }

        const hasStoredPermission = () => {
            return hasPermissionCookie() || hasPermissionSessionValue()
        }

        const setPermissionCookie = (value) => {
            if (!props.cookieName) {
                return
            }

            const expires = new Date()
            expires.setDate(expires.getDate() + props.cookieExpirationDays)
            document.cookie =
                `${props.cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
        }

        const setPermissionSessionValue = (value) => {
            if (!permissionStorageKey.value) {
                return
            }

            try {
                window.sessionStorage.setItem(permissionStorageKey.value, value)
            } catch {
                // Ignore session storage errors.
            }
        }

        const removePermissionSessionValue = () => {
            if (!permissionStorageKey.value) {
                return
            }

            try {
                window.sessionStorage.removeItem(permissionStorageKey.value)
            } catch {
                // Ignore session storage errors.
            }
        }

        const checkFunctionalConsent = () => {
            if (typeof window.CookieInformation?.getConsentGivenFor !== 'function') {
                const consentCookie = getCookieInformationConsent()
                cookieConsentGiven.value = consentCookie?.consents_approved?.includes('cookie_cat_functional') || false
                return
            }

            cookieConsentGiven.value =
                window.CookieInformation?.getConsentGivenFor('cookie_cat_functional') || false
        }

        const hasCookieBannerDecision = () => {
            const consentCookie = getCookieInformationConsent()

            if (consentCookie) {
                return true
            }

            if (typeof window.CookieInformation?.getConsentGivenFor !== 'function') {
                return false
            }

            if (typeof window.CookieInformation?.wasBannerConfirmed === 'function') {
                return window.CookieInformation.wasBannerConfirmed()
            }

            return cookieConsentGiven.value
        }

        const syncPermissionState = () => {
            checkFunctionalConsent()

            if (cookieConsentGiven.value && hasPermissionSessionValue() && !hasPermissionCookie()) {
                setPermissionCookie('true')
                removePermissionSessionValue()
            }

            if (hasStoredPermission()) {
                clearInitialStateTimers()
                return true
            }

            if (hasCookieBannerDecision()) {
                modalOpen.value = true
                clearInitialStateTimers()
                return true
            }

            return false
        }

        const startInitialStateSync = () => {
            if (initialStateIntervalId || hasStoredPermission() || modalOpen.value) {
                return
            }

            initialStateIntervalId = window.setInterval(() => {
                syncPermissionState()
            }, 250)

            initialStateTimeoutId = window.setTimeout(() => {
                clearInitialStateTimers()
            }, 5000)
        }

        const tabs = computed(() =>
            props.languageItems.map(lang => ({
                id: lang.languageCode,
                title: lang.titles?.[currentLocale.value] || lang.languageCode
            }))
        )

        const currentLabels = computed(() => props.labels[currentLocale.value] || {})

        const title = computed(() => currentLabels.value.title || '')
        const closeAriaLabel = computed(() => currentLabels.value.closeAriaLabel || 'Luk')
        const agreementText = computed(() => currentLabels.value.agreementText || '')
        const agreeButtonLabel = computed(() => currentLabels.value.confirmButtonLabel || 'Accepter')
        const consentsGroupLabel = computed(() => currentLabels.value.consentsGroupLabel || '')

        const currentConsents = computed(() =>
            props.consents.map(c => ({
                id: c.id,
                label: c.labels?.[currentLocale.value]?.text || ''
            }))
        )

        const allConsentsChecked = computed(() =>
            props.consents.length > 0 &&
            props.consents.every(c => consentValues.value[c.id])
        )

        const handleTabChange = (tabId) => {
            currentLocale.value = tabId
        }

        const handleCheckboxInput = (consentId, value) => {
            consentValues.value = { ...consentValues.value, [consentId]: value }
        }

        const handleAgree = () => {
            if (!allConsentsChecked.value) return

            if (cookieConsentGiven.value) {
                setPermissionCookie('true')
                removePermissionSessionValue()
            } else {
                setPermissionSessionValue('true')
            }

            modalOpen.value = false
            statusMessage.value = currentLabels.value.agreedStatusMessage || ''
        }

        const handleModalOpen = () => {
            tabsKey.value++
        }

        const handleModalClose = () => {
            modalOpen.value = false
            if (props.redirectUrl) {
                window.location.href = props.redirectUrl
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncPermissionState()
            }
        }

        onMounted(() => {
            initConsentValues()
            syncPermissionState()
            startInitialStateSync()

            window.addEventListener('CookieInformationAPIReady', syncPermissionState)
            window.addEventListener('CookieInformationConsentSubmitted', syncPermissionState)
            window.addEventListener('focus', syncPermissionState)
            window.addEventListener('pageshow', syncPermissionState)
            document.addEventListener('visibilitychange', handleVisibilityChange)
        })

        onUnmounted(() => {
            clearInitialStateTimers()
            window.removeEventListener('CookieInformationAPIReady', syncPermissionState)
            window.removeEventListener('CookieInformationConsentSubmitted', syncPermissionState)
            window.removeEventListener('focus', syncPermissionState)
            window.removeEventListener('pageshow', syncPermissionState)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        })

        return {
            modalOpen,
            currentLocale,
            cookieConsentGiven,
            consentValues,
            statusMessage,
            tabs,
            title,
            closeAriaLabel,
            agreementText,
            agreeButtonLabel,
            consentsGroupLabel,
            currentConsents,
            allConsentsChecked,
            tabsKey,
            handleTabChange,
            handleCheckboxInput,
            handleAgree,
            handleModalOpen,
            handleModalClose
        }
    }
}
